/**
 * Overdraft (découvert autorisé) helper — 10/02/2026 (Lot J).
 *
 * Demande user prod : « il faut avoir la possibilité d'être à découvert,
 * ce découvert est indiqué par la conseillère et peut évoluer en fonction
 * du relationnel et du scoring bancaire. »
 *
 * Modèle dynamique :
 *   Plafond = clamp(
 *     BASE + bonus(loyaltyLevel) + bonus(creditScore) + bonus(CA mensuel),
 *     0, MAX_OVERDRAFT_LIMIT
 *   )
 *
 *   • BASE : 1 000 € pour tout client avec relation BNB Mariba (>= 1 prêt)
 *   • Bonus loyaltyLevel BNB Mariba :  +1500 / +3500 / +6000 / +9000 €
 *   • Bonus creditScore grade : A=+5000 / B=+3000 / C=+1500 / D=0 / E=-2000 €
 *   • Bonus CA mensuel : 10 % du CA mensuel (max 5 000 €)
 *   • MAX_OVERDRAFT_LIMIT : 25 000 €
 *
 * Agios mensuels : taux annuel 12 % par défaut, +2 % par grade en dessous
 * de B, prorata journalier appliqué dans `computeDailyExpenses`. Au-delà
 * du plafond, le taux double (24 % / an minimum) pour modéliser un
 * dépassement « non autorisé ».
 */
const BASE_OVERDRAFT = 1000;
const MAX_OVERDRAFT_LIMIT = 25000;

const LOYALTY_BONUS = { 0: 0, 1: 1500, 2: 3500, 3: 6000, 4: 9000 };
const SCORE_BONUS = { A: 5000, B: 3000, C: 1500, D: 0, E: -2000 };

/**
 * @returns {{ limit: number, breakdown: object, hasRelation: boolean }}
 */
export function computeOverdraftLimit(state) {
  const rel = state?.bankRelations?.bnb_mariba || {};
  const hasRelation = (rel.activeLoanCount || 0) > 0 || (rel.loansRepaid || 0) > 0;
  if (!hasRelation) {
    return {
      limit: 0,
      breakdown: { base: 0, loyalty: 0, score: 0, revenue: 0 },
      hasRelation: false,
    };
  }
  const loyalty = LOYALTY_BONUS[rel.loyaltyLevel || 0] ?? 0;
  const grade = state?.creditScore?.grade || 'C';
  const score = SCORE_BONUS[grade] ?? 0;
  const monthlyRevenue = state?.monthlyRevenues || 0;
  const revenue = Math.min(5000, Math.round(monthlyRevenue * 0.10));
  const raw = BASE_OVERDRAFT + loyalty + score + revenue;
  const limit = Math.max(0, Math.min(MAX_OVERDRAFT_LIMIT, raw));
  return {
    limit,
    breakdown: { base: BASE_OVERDRAFT, loyalty, score, revenue },
    hasRelation: true,
  };
}

/**
 * Returns the daily agios fee given current treasury (negative) and the
 * authorised overdraft. The agios rate scales with the credit grade :
 *   A : 0.10 / an  (0.0003/day)
 *   B : 0.12 / an  (0.00033/day)
 *   C : 0.14 / an  (0.00038/day)
 *   D : 0.18 / an  (0.0005/day)
 *   E : 0.22 / an  (0.0006/day)
 *
 * Dépassement (utilisé > plafond) : taux ×2 sur la part au-delà du plafond.
 */
const DAILY_AGIOS_RATE_BY_GRADE = { A: 0.00027, B: 0.00033, C: 0.00038, D: 0.0005, E: 0.0006 };

export function computeDailyAgios(state) {
  const t = state?.treasury || 0;
  if (t >= 0) return { agios: 0, withinLimit: 0, overLimit: 0, rate: 0 };
  const { limit } = computeOverdraftLimit(state);
  const used = -t; // positive
  const withinLimit = Math.min(used, limit);
  const overLimit = Math.max(0, used - limit);
  const grade = state?.creditScore?.grade || 'C';
  const rate = DAILY_AGIOS_RATE_BY_GRADE[grade] ?? DAILY_AGIOS_RATE_BY_GRADE.C;
  const agiosWithin = withinLimit * rate;
  const agiosOver = overLimit * rate * 2;
  return {
    agios: Math.max(5, Math.round(agiosWithin + agiosOver)),
    withinLimit, overLimit, rate,
  };
}

/**
 * Used by UI : returns a one-line summary string for the sidebar treasury
 * chip indicating overdraft status.
 *
 * Examples :
 *   - « Découvert autorisé : 4 500 € »
 *   - « Découvert utilisé : -2 100 € / 4 500 € autorisés »
 *   - « ⚠ Dépassement : -7 800 € (limite 4 500 €) — agios doublés »
 */
export function overdraftSummary(state) {
  const t = state?.treasury || 0;
  const { limit, hasRelation } = computeOverdraftLimit(state);
  if (!hasRelation) return null;
  if (t >= 0) return { tone: 'idle', text: `Découvert autorisé : ${limit.toLocaleString('fr-FR')} €`, limit, used: 0 };
  const used = -t;
  if (used <= limit) {
    return {
      tone: 'warning',
      text: `Découvert : -${used.toLocaleString('fr-FR')} € / ${limit.toLocaleString('fr-FR')} € autorisés`,
      limit, used,
    };
  }
  return {
    tone: 'danger',
    text: `⚠ Dépassement : -${used.toLocaleString('fr-FR')} € (limite ${limit.toLocaleString('fr-FR')} €)`,
    limit, used,
  };
}
